import { useState, useCallback } from 'react';
import {
  createSessionPartnership,
  mergePartnershipIntoList,
  PARTNERSHIP_RPC_CODES,
} from '../services/sessionPartnershipService';
import {
  buildLocalSessionPartnership,
  findLocalPartnershipConflict,
} from '../utils/session/buildLocalSessionPartnership';
import { OFFLINE_COMMAND_TYPES, shouldRunLocalHostPath } from '../utils/session/offlineQueueConstants';
import { removeSessionPartnershipMaybeOffline } from '../utils/session/removeSessionPartnershipMaybeOffline';

function getPlayerDisplayName(playersById, playerId) {
  const player = playersById?.[playerId];
  if (!player) return 'Unknown player';
  if (player.name) return player.name;
  return [player.first_name, player.last_name].filter(Boolean).join(' ') || 'Unknown player';
}

/**
 * Shared create-partnership flow with conflict modal support.
 */
export default function useSessionPartnershipCreate({
  sessionId,
  partnerships = [],
  onUpdatePartnerships,
  playersById = {},
  onToast,
  loadSessionPartnerships,
  offlineQueue = null,
  session = null,
  getSnapshot = null,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [pendingCreate, setPendingCreate] = useState(null);

  const closeConflict = useCallback(() => {
    setConflict(null);
    setPendingCreate(null);
  }, []);

  const applySuccess = useCallback(
    (result, onSuccess) => {
      const partnership = result.data;

      // Close modal / run caller cleanup before list update to avoid a flash of
      // "insufficient players" UI when the new pair leaves the available pool.
      onSuccess?.(partnership);

      const next = mergePartnershipIntoList(partnerships, partnership, result.code);
      onUpdatePartnerships(next);

      if (result.code === PARTNERSHIP_RPC_CODES.PAIR_REACTIVATED) {
        onToast?.({ message: 'Partnership restored.', variant: 'success' });
      } else if (result.code === PARTNERSHIP_RPC_CODES.PAIR_ALREADY_ACTIVE) {
        onToast?.({ message: 'This partnership is already active.', variant: 'info' });
      } else if (result.code === PARTNERSHIP_RPC_CODES.CREATED) {
        onToast?.({ message: 'Partnership created.', variant: 'success' });
      }
    },
    [partnerships, onUpdatePartnerships, onToast]
  );

  const createPartnership = useCallback(
    async (player1Id, player2Id, { onSuccess } = {}) => {
      if (!sessionId || !player1Id || !player2Id) {
        return { success: false, error: 'Missing session or player selection.' };
      }

      setIsLoading(true);
      try {
        if (shouldRunLocalHostPath(offlineQueue, session)) {
          const localConflict = findLocalPartnershipConflict(partnerships, player1Id, player2Id);
          if (localConflict?.kind === 'already_active') {
            const result = {
              success: true,
              code: PARTNERSHIP_RPC_CODES.PAIR_ALREADY_ACTIVE,
              data: localConflict.partnership,
            };
            applySuccess(result, onSuccess);
            closeConflict();
            return result;
          }
          if (localConflict?.kind === 'already_partnered') {
            setConflict(localConflict.conflict);
            setPendingCreate({ player1Id, player2Id, onSuccess });
            return {
              success: false,
              code: PARTNERSHIP_RPC_CODES.PLAYER_ALREADY_PARTNERED,
              conflict: localConflict.conflict,
            };
          }

          const partnership = buildLocalSessionPartnership(player1Id, player2Id);
          const result = {
            success: true,
            code: PARTNERSHIP_RPC_CODES.CREATED,
            data: partnership,
          };
          applySuccess(result, onSuccess);
          closeConflict();
          const next = mergePartnershipIntoList(partnerships, partnership, result.code);
          await offlineQueue.enqueue(
            {
              type: OFFLINE_COMMAND_TYPES.PARTNERSHIP_ADD,
              player1Id,
              player2Id,
              partnershipId: partnership.id,
            },
            { ...(getSnapshot?.() || {}), partnerships: next }
          );
          return result;
        }

        const result = await createSessionPartnership(sessionId, player1Id, player2Id);

        if (result.success) {
          applySuccess(result, onSuccess);
          closeConflict();
          return result;
        }

        if (result.code === PARTNERSHIP_RPC_CODES.PLAYER_ALREADY_PARTNERED && result.conflict) {
          setConflict(result.conflict);
          setPendingCreate({ player1Id, player2Id, onSuccess });
          return result;
        }

        onToast?.({
          message: result.error || 'Failed to create partnership.',
          variant: 'error',
        });
        return result;
      } catch (err) {
        onToast?.({
          message: err.message || 'Failed to create partnership.',
          variant: 'error',
        });
        return { success: false, error: err.message };
      } finally {
        setIsLoading(false);
      }
    },
    [
      sessionId,
      partnerships,
      applySuccess,
      closeConflict,
      onToast,
      offlineQueue,
      session,
      getSnapshot,
    ]
  );

  const resolveConflictByRemovingExisting = useCallback(async () => {
    if (!sessionId || !conflict?.partnership || !pendingCreate) return;

    const { player1Id, player2Id, onSuccess } = pendingCreate;
    const existing = conflict.partnership;

    setIsLoading(true);
    try {
      const removeResult = await removeSessionPartnershipMaybeOffline({
        sessionId,
        player1Id: existing.player1Id,
        player2Id: existing.player2Id,
        offlineQueue,
        session,
        snapshot: getSnapshot?.() || null,
      });

      if (!removeResult.success) {
        onToast?.({
          message: removeResult.error || 'Could not remove existing partnership.',
          variant: 'error',
        });
        return;
      }

      const updatedList = partnerships.filter((p) => p.id !== existing.id);
      onUpdatePartnerships(updatedList);

      if (loadSessionPartnerships && !shouldRunLocalHostPath(offlineQueue, session)) {
        await loadSessionPartnerships();
      }

      closeConflict();
      await createPartnership(player1Id, player2Id, { onSuccess });
    } finally {
      setIsLoading(false);
    }
  }, [
    sessionId,
    conflict,
    pendingCreate,
    partnerships,
    onUpdatePartnerships,
    loadSessionPartnerships,
    closeConflict,
    createPartnership,
    onToast,
    offlineQueue,
    session,
    getSnapshot,
  ]);

  const blockedPlayerName = conflict?.playerId
    ? getPlayerDisplayName(playersById, conflict.playerId)
    : 'This player';
  const existingPartnerName = conflict?.existingPartnerId
    ? getPlayerDisplayName(playersById, conflict.existingPartnerId)
    : 'their current partner';

  return {
    isLoading,
    createPartnership,
    closeConflict,
    resolveConflictByRemovingExisting,
    conflictModalProps: {
      isOpen: Boolean(conflict),
      onClose: closeConflict,
      blockedPlayerName,
      existingPartnerName,
      onRemoveExisting: resolveConflictByRemovingExisting,
      isLoading,
    },
  };
}
